import {
	ActionListItem,
	ActionListSearch,
	ActionListSearchHandle,
	ActionListSubMenuHeader,
	DropOverActionList,
	NoResults,
	SearchPlaceholder
} from "client/Components";
import {
	ReactElement,
	RefObject,
	useCallback,
	useMemo,
	useRef,
	useState
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { GlobalNavigationItemKind } from "client/Api/TitleBar/GlobalNavigation/globalNavigation";
import { isNil, isNotNil } from "client/Misc/utils";
import { contains, normalizeForSearch } from "client/Data/searching";
import {
	KeyBoardListNavigationContext,
	OpenItemMenuState,
	useItemDropOverMenuProps,
	useKeyboardListNavigation,
	useKeyboardListNavigationItem
} from "client/Hooks";

export type GlobalNavigationCommonItemProps = {
	target: HTMLElement;
	containingGroupIndex: number | null;
	groups: readonly GlobalNavigationGroupAndIndex[];
	onMoveToGroup(groupIndex: number): void;
	onMoveOutOfGroup?(): void;
	menuTitle: string;
	onClose: () => void;
	menuState: OpenItemMenuState;
};

export type GlobalNavigationAppItemMenuProps = {
	kind: GlobalNavigationItemKind.InstalledApp;
	onDelete: () => void;
};

export type GlobalNavigationSpaceItemMenuProps = {
	kind: GlobalNavigationItemKind.Space;
	onDelete?: () => void;
	onEdit?: () => void;
};

export type GlobalNavigationItemMenuProps =
	GlobalNavigationCommonItemProps
	& (
		| GlobalNavigationAppItemMenuProps
		| GlobalNavigationSpaceItemMenuProps
	);

export const GlobalNavigationItemMenu = (props: GlobalNavigationItemMenuProps): ReactElement => {
	const {
		target,
		groups,
		containingGroupIndex,
		onDelete,
		onMoveToGroup,
		onMoveOutOfGroup,
		menuTitle,
		onClose,
		menuState
	} = props;

	const [section, setSection] = useState<Section>("main");

	const {
		contentAlignment,
		targetAlignment,
		closeOnMouseDown
	} = useItemDropOverMenuProps(
		menuState,
		{
			contextMenuContentAlignment: "tl",
			moreMenuContentAlignment: "tr",
			moreMenuTargetAlignment: "mc"
		}
	);

	const showMoveToGroupSubMenuHandler = useCallback(
		(): void => {
			setSection("group-selection");
		},
		[]
	);

	const moveToGroupTitle: string = localize(
		isNil(containingGroupIndex)
			? "_MoveToGroup"
			: "_MoveToAnotherGroup"
	);

	const groupsForSelection = useMemo(
		(): readonly GlobalNavigationGroupAndIndex[] => {
			if (isNil(containingGroupIndex)) {
				return groups;
			}

			return groups.filter(g => g.index !== containingGroupIndex);
		},
		[containingGroupIndex, groups]
	);

	return (
		<DropOverActionList
			target={target}
			onClose={onClose}
			contentAlignment={contentAlignment}
			targetAlignment={targetAlignment}
			menuTitle={menuTitle}
			closeOnMouseDown={closeOnMouseDown}
		>
			{section === "main"
				? (
					<>
						{isNotNil(containingGroupIndex) && isNotNil(onMoveOutOfGroup) && (
							<ActionListItem
								onClick={onMoveOutOfGroup}
								text={localize("_MoveOutOfGroup")}
								trackingId="move-out-of-group"
								icon="arrow-left-to-line"
							/>
						)}
						{groupsForSelection.length > 0 && (
							<ActionListItem
								onClick={showMoveToGroupSubMenuHandler}
								text={moveToGroupTitle}
								trackingId="move-to-group"
								icon="arrow-right-to-line"
								rightIcon="chevron-right"
								shouldNotAutoCloseContainingDropOver
							/>
						)}
						{ (props.kind === GlobalNavigationItemKind.Space && props.onEdit) && (
							<ActionListItem
								onClick={props.onEdit}
								text={localize("_Edit")}
								trackingId="edit"
								icon="pen"
							/>
						)}
						{ onDelete && (
							<ActionListItem
								onClick={onDelete}
								text={localize("_Delete")}
								trackingId="delete"
								icon="trash-can"
							/>
						)}
					</>
				)
				: (
					<GroupSelectionMenu
						groups={groupsForSelection}
						moveToGroupTitle={moveToGroupTitle}
						onMoveToGroup={onMoveToGroup}
						onGoBack={() => setSection("main")}
					/>
				)
			}
		</DropOverActionList>
	);
};

GlobalNavigationItemMenu.displayName = "GlobalNavigationItemMenu";

type GroupSelectionMenuProps = {
	groups: readonly GlobalNavigationGroupAndIndex[];
	moveToGroupTitle: string;
	onMoveToGroup: (groupIndex: number) => void;
	onGoBack: () => void;
};

const GroupSelectionMenu = (props: GroupSelectionMenuProps): ReactElement => {
	const {
		groups,
		moveToGroupTitle,
		onMoveToGroup,
		onGoBack
	} = props;

	const [searchTerm, setSearchTerm] = useState<string>("");
	const searchInputRef: RefObject<ActionListSearchHandle | null> = useRef<ActionListSearchHandle>(null);

	const normalizedFilter: string = normalizeForSearch(searchTerm);

	const groupsToRender = useMemo(
		(): readonly GlobalNavigationGroupAndIndex[] => {
			if (!normalizedFilter) {
				return groups;
			}

			return groups.filter(g => contains(g.name, normalizedFilter));
		},
		[groups, normalizedFilter]
	);

	const {
		focusFirstItemHandler,
		contextData
	} = useKeyboardListNavigation({
		onFocusInput: () => {
			searchInputRef.current
				?.focusInput();
		}
	});

	return (
		<>
			<ActionListSubMenuHeader
				text={moveToGroupTitle}
				onGoBack={onGoBack}
			/>
			<ActionListSearch
				onChange={setSearchTerm}
				icon="filter"
				placeholder={
					<SearchPlaceholder
						kind="filter"
						properties={[localize("_Group")]}
					/>
				}
				onNavigateToResults={focusFirstItemHandler}
				ref={searchInputRef}
			/>
			<KeyBoardListNavigationContext.Provider
				value={contextData}
			>
				{groupsToRender.length === 0
					? (
						<NoResults
							icon="magnifying-glass"
							title={localize("_NoSearchResults")}
							message={localize("_PleaseTrySearchingWithAnotherTerm")}
							trackingId="no-search-results"
						/>
					)
					: groupsToRender.map((g, i) => (
						<ListItem
							name={g.name}
							onSelect={onMoveToGroup}
							key={g.index}
							index={g.index}
							renderIndex={i}
						/>
					))
				}
			</KeyBoardListNavigationContext.Provider>
		</>
	);
};
GroupSelectionMenu.displayName = "GroupSelectionMenu";

type ListItemProps = {
	index: number;
	renderIndex: number;
	onSelect: (index: number) => void;
	name: string;
};

const ListItem = (props: ListItemProps): ReactElement => {
	const {
		index,
		renderIndex,
		onSelect,
		name
	} = props;

	const selectHandler = useCallback(
		(): void => {
			onSelect(index);
		},
		[index, onSelect]
	);

	const {
		onKeyDown,
		ref
	} = useKeyboardListNavigationItem({
		itemIndex: renderIndex,
		onExecute: selectHandler
	});

	return (
		<ActionListItem
			ref={ref}
			onClick={selectHandler}
			text={name}
			onKeyDown={onKeyDown}
			tabIndex={-1}
		/>
	);
};
ListItem.displayName = "ListItem";

type Section = "main" | "group-selection";

export type GlobalNavigationGroupAndIndex = {
	name: string;
	index: number;
};